// Replyables Module. Packages up the to/cc/bcc from a Change
//
import PropTypes from 'prop-types'
import {
  isAgentResponse,
  getLatestReply,
  isReply,
  isUserChange,
} from 'util/changesets'
import { any, findFirst } from 'util/arrays'
import { getValueByPath, isEmpty } from 'util/objects'
import { memoize } from 'util/memoization'

const actorType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired, // GR: might be a bit restrictive i.e. twitter tickets
})

const isActor = actor => {
  // very loose check - just checks if its not nil/empty
  return actor && !isEmpty(actor)
}

// borrows a convention for naming a module struct’s type `t`.
const t = PropTypes.shape({
  to: actorType,
  cc: PropTypes.arrayOf(actorType),
  bcc: PropTypes.arrayOf(actorType),
})

const build = memoize((to = [], cc = [], bcc = []) => {
  if (to.length === 0 && cc.length === 0 && bcc.length === 0) return undefined
  const replyables = {}
  if (to.length > 0) replyables.to = to
  if (cc.length > 0) replyables.cc = cc
  if (bcc.length > 0) replyables.bcc = bcc

  return replyables
})

const getTo = (action = {}) => {
  if (isAgentResponse(action.change)) {
    return action.change &&
      Array.isArray(action.change.to) &&
      action.change.to.length > 0
      ? action.change.to[0]
      : action.change.to
  }
  return action.actor
}

function fromAction(action, { overrideTo } = {}) {
  const { change } = action

  const to = []
  const toFromAction = getTo(action)
  if (isActor(overrideTo)) {
    to.push(overrideTo)
  } else if (isActor(toFromAction)) {
    to.push(toFromAction)
  }

  const cc = change.cc
  const bcc = change.bcc

  return build(to, cc, bcc)
}

function isSameActor(firstAction = {}, lastAction = {}) {
  const first = getTo(firstAction)
  const last = getTo(lastAction)
  return first && last && first.id && last.id && first.id === last.id
}

// Finds the latest reply, or a specific one by Id
function findReply(actions, changesetId) {
  if (changesetId) {
    const foundReply = findFirst(
      actions.filter(isReply),
      a => a.changeset === changesetId
    )
    if (foundReply) return foundReply
  }

  return getLatestReply(actions)
}

function fromActionList(actions, customer, changesetId = undefined) {
  const lastReply = findReply(actions, changesetId)

  if (!lastReply) return null

  const hasUserChanged = any(isUserChange, actions)

  if (!hasUserChanged) return fromAction(lastReply)

  // At this point, we know the Customer/User was changed on this ticket
  // We make the assumption that the original/first message user is the ticket.customer.
  // So if this latest message was from that first message user, then we take the
  // ticket.customer as the person to reply to
  const firstReply = findFirst(actions, isReply)

  if (!isSameActor(firstReply, lastReply)) return fromAction(lastReply)

  return fromAction(lastReply, { overrideTo: customer })
}

// The main API. Figures out who the damn reply should go to!
function fromTicket(ticket, changesetId = undefined) {
  if (!ticket) return undefined

  const actions = getValueByPath('actions.records', ticket) || []
  const { customer } = ticket

  if (!getLatestReply(actions)) {
    const to = []

    if (isActor(customer)) to.push(customer)

    return build(to)
  }

  return fromActionList(actions, customer, changesetId)
}

export default {
  t,
  fromTicket,
}
